// Trophy catalog + unlock helpers.
//
// Storage strategy:
//   • localStorage (`crt_trophies_v1`) is the authoritative source for the
//     UI — reading it is synchronous, which is what every consumer expects
//     (`getUnlocked()` is called during render).
//   • When the viewer is signed in (Emergent Google Auth), unlocks are
//     ALSO POSTed to the backend so the trophy follows the user across
//     devices. `hydrateFromServer()` merges the server set INTO
//     localStorage at sign-in time, and `syncPendingToServer()` pushes any
//     pre-login unlocks up to the account so history isn't lost when a
//     viewer signs in for the first time.
//   • Signed-out viewers still get the full flow — trophies just stay
//     device-local until they sign in.
const STORAGE_KEY = "crt_trophies_v1";
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const TROPHIES = [
  { id: "channel_surfer", name: "CHANNEL SURFER",
    desc: "Hopped channels 20 times in a single session." },
  { id: "night_owl",      name: "NIGHT OWL",
    desc: "Tuned in between 1 and 4 AM local time." },
  { id: "early_bird",     name: "EARLY BIRD",
    desc: "Caught the morning broadcast (5–7 AM)." },
  { id: "first_voice",    name: "FIRST VOICE",
    desc: "Sent your first chat message." },
  { id: "broadcaster",    name: "BROADCASTER",
    desc: "Shared a deep-link from the CRT." },
  { id: "code_breaker",   name: "CODE BREAKER",
    desc: "Typed the secret Konami sequence." },
  { id: "completionist",  name: "COMPLETIONIST",
    desc: "Visited every real channel in one sitting." },
  { id: "loud_and_proud", name: "LOUD AND PROUD",
    desc: "Cranked the remote VOL to the maximum." },
  { id: "patron",         name: "★ PATRON",
    desc: "Bought the station operator a coffee." },
];

const trophyById = Object.fromEntries(TROPHIES.map((t) => [t.id, t]));

function readSet() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (e) { return new Set(); }
}

function writeSet(set) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...set])); }
  catch (e) { /* localStorage full or denied — silently noop */ }
}

export function getUnlocked() {
  return readSet();
}

// POST an unlocked id to the backend, best-effort. Cookies carry the
// session, so `credentials: "include"` is essential. We deliberately don't
// await this on the hot path — the local write is what powers the UI, the
// server write is just for cross-device persistence and can fail silently.
function pushToServer(id) {
  try {
    fetch(`${API}/viewer/trophies/unlock`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trophy_id: id }),
    }).catch(() => { /* not signed in / offline — trophy stays local */ });
  } catch (e) { /* noop */ }
}

// Unlock a trophy. Returns the trophy descriptor if this call actually
// unlocked it (so callers can fire a toast); returns null if it was already
// owned or the id is unknown. Fires a server-side unlock in parallel when
// the viewer is signed in (best-effort — the server call may 401 for
// anonymous viewers and that's fine).
export function unlock(id) {
  const meta = trophyById[id];
  if (!meta) return null;
  const set = readSet();
  if (set.has(id)) return null;
  set.add(id);
  writeSet(set);
  pushToServer(id);
  return meta;
}

export function resetAll() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
}

// Hydrate the local set with the server's persisted trophies AND push any
// pre-login local unlocks up to the account in one round-trip. Called from
// App.js whenever the auth state flips to signed-in so a viewer who's
// signed in on a fresh device gets their PATRON badge back immediately.
// Silently noops on 401/network errors — anonymous viewers keep their
// local set untouched.
export async function hydrateFromServer() {
  try {
    const local = [...readSet()];
    // Push local → server first so any anonymous unlocks earned before
    // this sign-in aren't discarded when we then read the server list.
    if (local.length) {
      await fetch(`${API}/viewer/trophies/sync`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: local }),
      });
    }
    const res = await fetch(`${API}/viewer/trophies`, { credentials: "include" });
    if (!res.ok) return false;
    const body = await res.json();
    const remote = Array.isArray(body && body.unlocked) ? body.unlocked : [];
    if (!remote.length) return true;
    // Union server → local so subsequent renders show the badge.
    const merged = new Set([...local, ...remote]);
    writeSet(merged);
    return true;
  } catch (e) {
    return false;
  }
}
